import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../';
import { 
  Organization, 
  Team, 
  Member, 
  Invitation,
  Subscription,
  UsageMetrics,
  UsageHistory,
  Invoice,
  PaymentMethod
} from '../../types';

export const organizationApi = {
  // Organization endpoints
  getAll: async (includePending: boolean = false, page: number = 1, limit: number = 10) => {
    return await userService.getUserOrganizations();
  },
  
  getById: async (id: string) => {
    return await organizationService.getOrganization(id);
  },
  
  create: async (data: { name: string; description?: string; slug?: string }) => {
    return await organizationService.createOrganization(data);
  },
  
  update: async (id: string, data: { name?: string; description?: string }) => {
    return await organizationService.updateOrganization(id, data);
  },
  
  delete: async (id: string) => {
    return await organizationService.deleteOrganization(id);
  },
  
  getOverview: async (id: string) => {
    return await organizationService.getOrganizationOverview(id);
  },
  
  leave: async (id: string, reason?: string) => {
    return await organizationService.leaveOrganization(id, reason);
  },
  
  // Team endpoints
  getTeams: async () => {
    return await userService.getUserTeams();
  },
  
  getTeamById: async (id: string, organizationId: string) => {
    return await teamService.getTeamById(id, organizationId);
  },
  
  createTeam: async (data: { name: string; description?: string; organizationId: string; subscription_id?: string; slug?: string }) => {
    return await teamService.createTeam(data);
  },
  
  updateTeam: async (id: string, data: { name?: string; description?: string }) => {
    return await teamService.updateTeam(id, data);
  },
  
  getTeamOverview: async (id: string) => {
    return await teamService.getTeamOverview(id);
  },
  
  getTeamMembers: async (id: string, page: number = 1, limit: number = 20, role?: string) => {
    return await teamService.getTeamMembers(id, page, limit, role);
  },
  
  leaveTeam: async (id: string, reason?: string) => {
    return await teamService.leaveTeam(id, reason);
  },
  
  // User endpoints
  getUserTeams: async () => {
    return await userService.getUserTeams();
  },
  
  getUserInvitations: async (status?: string, page: number = 1, limit: number = 10) => {
    return await userService.getUserInvitations(status, page, limit);
  },
  
  getUserSentInvitations: async (status?: string, page: number = 1, limit: number = 10) => {
    return await invitationService.getUserSentInvitations(status, page, limit);
  },
  
  // Invitation endpoints
  createInvitation: async (organizationId: string, data: { email: string; role: string; team_id?: string; message?: string; expires_at?: string }) => {
    return await invitationService.createInvitation(organizationId, data);
  },
  
  listInvitations: async (organizationId: string, status?: string, teamId?: string, page: number = 1, limit: number = 10) => {
    return await invitationService.listInvitations(organizationId, { status, teamId, page, limit } as any);
  },
  
  getInvitationById: async (id: string) => {
    return await invitationService.getInvitationById(id);
  },
  
  getInvitationByToken: async (token: string) => {
    return await invitationService.getInvitationByToken(token);
  },
  
  acceptInvitationById: async (id: string) => {
    return await invitationService.acceptInvitationById(id);
  },
  
  acceptInvitationByToken: async (token: string) => {
    return await invitationService.acceptInvitationByToken(token);
  },
  
  declineInvitationById: async (id: string, reason?: string) => {
    return await invitationService.declineInvitationById(id, reason);
  },
  
  declineInvitationByToken: async (token: string, reason?: string) => {
    return await invitationService.declineInvitationByToken(token, reason);
  },
  
  cancelInvitation: async (id: string, reason?: string) => {
    return await invitationService.cancelInvitation(id, reason);
  },
  
  // Subscription related endpoints
  getSubscriptions: async (organizationId: string): Promise<Subscription[]> => {
    return await organizationService.getOrganizationSubscriptions(organizationId) as any;
  },
  
  createSubscription: async (data: { 
    name: string; 
    planId: string; 
    autoRenew?: boolean;
    organizationId: string 
  }): Promise<Subscription> => {
    // Use planId to match updated API service parameter
    const result = await subscriptionService.createSubscription({
      name: data.name,
      planId: data.planId,
      autoRenew: data.autoRenew ?? true,
      organizationId: data.organizationId
    });
    // Type assertion for interface compatibility
    return {
      ...result,
      teams: (result as any).teams || [],
      limits: (result as any).limits || {},
      nextBillingDate: (result as any).nextBillingDate || '',
      createdAt: (result as any).createdAt || new Date().toISOString(),
      updatedAt: (result as any).updatedAt || new Date().toISOString()
    } as any;
  },
  
  cancelSubscription: async (id: string): Promise<Subscription> => {
    const result = await subscriptionService.cancelSubscription(id);
    // Type assertion for interface compatibility
    return {
      ...result,
      teams: (result as any).teams || [],
      limits: (result as any).limits || {},
      nextBillingDate: (result as any).nextBillingDate || '',
      createdAt: (result as any).createdAt || new Date().toISOString(),
      updatedAt: (result as any).updatedAt || new Date().toISOString()
    } as any;
  },
  
  updateSubscriptionAddOns: async (id: string, addOns: { 
    extraUsers?: number; 
    extraStorage?: number;
    extraTeams?: number;
    extraApiCalls?: number
  }): Promise<Subscription> => {
    const result = await subscriptionService.updateSubscriptionAddOns(id, addOns as any);
    // Type assertion for interface compatibility
    return {
      ...result,
      teams: (result as any).teams || [],
      limits: (result as any).limits || {},
      nextBillingDate: (result as any).nextBillingDate || '',
      createdAt: (result as any).createdAt || new Date().toISOString(),
      updatedAt: (result as any).updatedAt || new Date().toISOString()
    } as any;
  },
  
  // Note: These subscription assignment methods are not available in current service implementation
  // assignSubscriptionToTeam: async (subscriptionId: string, teamId: string): Promise<void> => {
  //   return await subscriptionService.assignSubscriptionToTeam(subscriptionId, teamId);
  // },
  // 
  // unassignSubscriptionFromTeam: async (subscriptionId: string, teamId: string): Promise<void> => {
  //   return await subscriptionService.unassignSubscriptionFromTeam(subscriptionId, teamId);
  // },
  
  // Usage related endpoints
  getUsageMetrics: async (organizationId: string): Promise<UsageMetrics> => {
    return await subscriptionService.getUsageMetrics(organizationId) as any;
  },
  
  getUsageHistory: async (organizationId: string, resourceType: 'api_calls' | 'storage' | 'users' | 'teams', months: number = 6): Promise<UsageHistory[]> => {
    return await subscriptionService.getUsageHistory(organizationId, resourceType, months) as any;
  },
  
  // Billing related endpoints
  getInvoices: async (organizationId: string): Promise<Invoice[]> => {
    const response = await subscriptionService.listInvoices(organizationId);
    return response.invoices as any;
  },
  
  getPaymentMethods: async (organizationId: string): Promise<PaymentMethod[]> => {
    return await paymentService.listPaymentMethods(organizationId) as any;
  },
  
  addPaymentMethod: async (organizationId: string, paymentMethodId: string, setAsDefault: boolean = true): Promise<{ paymentMethod: PaymentMethod }> => {
    return await paymentService.addPaymentMethod(organizationId, paymentMethodId, setAsDefault) as any;
  },
  
  getSetupIntent: async (organizationId: string): Promise<{ clientSecret: string }> => {
    return await paymentService.createSetupIntent(organizationId);
  }
};

export default organizationApi;
